import { createStyles, makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import { useCallback, useEffect, useState } from "react";
import { fetchCareCoordinatorReferrals } from "../../../api/care_coordinator_referrals";
import ReferralInfo from "components/referrals/components/ReferralInfo";
import { CaseManagementReferralAdapter } from "components/referrals/adapters/CaseManagementReferralAdapter";
import { Homeless, User } from "types";
import { useSelector } from "react-redux";

const useStyles = makeStyles(() =>
  createStyles({
    rootWrapper: {
      position: "relative",
      margin: "16px 5% 32px 5%",
      minHeight: 0,
      overflow: "visible", 
      boxShadow: "0px 8px 16px 4px rgba(0,0,0,0.05)",
      borderRadius: 20,
    },
    accentCard: {
      position: "absolute",
      width: "50%",
      height: "100%",
      background: "#E1625E",
      borderRadius: 20,
      zIndex: 0,
      pointerEvents: "none",
    },
    rootCard: {
      position: "relative",
      background: "#F8E8E8",
      borderRadius: 20,
      padding: 24,
      paddingLeft: 32,
      paddingRight: 32,
      fontWeight: 500,
      zIndex: 1,
      marginLeft: 8, 
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    referralRequiredTitle: {
      fontSize: 20,
      fontWeight: 700,
      marginBottom: 4,
      color: "#E1625E",
    },
    referralRequiredDescription: {
      color: "#E1625E",
      marginBottom: 32,
      fontSize: 20,
      fontWeight: 300,
    },
    referralRequiredButton: {
      background: "#E1625E !important",
      color: "#fff !important",
      borderRadius: 999,
      padding: "8px 24px",
      fontFamily: "Manrope, sans-serif",
      fontWeight: 700,
      fontSize: 18,
      alignSelf: "flex-start",
      "&:hover": {
        background: "#b83b3b !important",
        boxShadow: "none",
      },
      textTransform: "none",
    },
    skeletonWrapper: {
      margin: "16px 5% 32px 5%",
    },
    // Styles for created referral state
    ctpRootCard: {
      position: "relative",
      background: "#FFFFFF",
      borderRadius: 20,
      padding: 24,
      paddingLeft: 32,
      paddingRight: 32,
      fontWeight: 500,
      zIndex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      border: "1px solid #E3E3FA",
    },
    ctpTitle: {
      fontSize: 20,
      fontWeight: 700,
      marginBottom: 24,
      color: "#1F2152",
      display: "flex",
      alignItems: "center",
      gap: "16px"
    },
    ctpButton: {
      color: "#7378E8 !important",
      borderRadius: 8,
      padding: "12px 24px",
      fontFamily: "Manrope, sans-serif",
      fontWeight: 500,
      fontSize: 20,
      alignSelf: "stretch",
      border: "1px solid #7378E8 !important",
      "&:hover": {
        background: "#F5F5F5 !important",
        boxShadow: "none",
      },
      textTransform: "none",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 24,
      marginTop: 32,
    },
    referralCount: {
      width: 24,
      height: 24,
      borderRadius: "50%",
      backgroundColor: "#7A6F06",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: 14,
    }
  })
);

type CareCoordinatorReferralBannerCardProps = {
  onSendReferral: () => void;
  onViewAllReferrals: () => void;
  homeless: Homeless;
  user: User;
  onHideBanner?: () => void;
};

function CareCoordinatorReferralBannerCard({ 
  onSendReferral, 
  onViewAllReferrals,
  homeless,
  user,
  onHideBanner
}: CareCoordinatorReferralBannerCardProps) {
  const classes = useStyles();
  const [showBanner, setShowBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [ctpReferrals, setCtpReferrals] = useState<any[]>([]);
  const HEADER_TITLE_TEXT = 'Referral Required';
  const HEADER_BODY_TEXT = `You've been assigned to send a referral for ${homeless.first_name || homeless.name} to a case manager.`;
  
  // Listen to memberReferrals updates
  const memberReferrals = useSelector((state: any) => state.referrals.memberReferrals);

  const checkReferrals = useCallback(async () => {
    try {
      console.log('fetching care coordinator referrals');
      const response = await fetchCareCoordinatorReferrals({
        homelessId: homeless.id,
        refereeId: user.id
      });
      const data = await response.json();
      
      // Check for active care coordinator referrals
      const activeReferrals = data.care_coordinator_referrals?.filter(
        (referral: { is_active: boolean }) => referral.is_active
      );
      
      // If there are active referrals, collect all their CTP caseload referrals
      const allCTPCaseloadReferrals = activeReferrals
        ?.flatMap((referral: { ctp_caseload_referrals?: any[] }) => referral.ctp_caseload_referrals || [])
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      const shouldShow = activeReferrals?.length > 0 && allCTPCaseloadReferrals.length === 0;
      setShowBanner(shouldShow);
      setCtpReferrals(allCTPCaseloadReferrals || []);
      // If no active referrals and we have the callback, notify parent to hide banner
      if (!shouldShow && onHideBanner) {
        onHideBanner();
      }
    } catch (error) {
      console.error('Failed to fetch care coordinator referrals:', error);
      setShowBanner(false);
      // Also notify parent on error
      if (onHideBanner) {
        onHideBanner();
      }
    } finally {
      setIsLoading(false);
    }
  }, [homeless.id, user.id, onHideBanner]);

  // Initial load
  useEffect(() => {
    checkReferrals();
  }, [homeless.id, user.id, checkReferrals]);

  // Refresh when memberReferrals updates
  useEffect(() => {
    if (showBanner) {
      checkReferrals();
    }
  }, [memberReferrals, showBanner, checkReferrals]);

  const renderReferralRequired = () => (
    <div className={classes.rootWrapper}>
      <div className={classes.accentCard} />
      <div className={classes.rootCard}>
        <div className={classes.referralRequiredTitle}>{HEADER_TITLE_TEXT}</div>
        <div className={classes.referralRequiredDescription}>
        {HEADER_BODY_TEXT}
        </div>
        <Button 
          className={classes.referralRequiredButton}
          onClick={onSendReferral}
        >
          Send Referral &nbsp; &gt;
        </Button>
      </div>
    </div>
  );

  const renderReferralCreated = () => (
    <div className={classes.rootWrapper}>
      <div className={classes.ctpRootCard}>
        <div className={classes.ctpTitle}>
          <span>Your Referral{ctpReferrals.length > 1 ? 's are' : ' is'} Pending</span>
          {ctpReferrals.length > 1 && <div className={classes.referralCount}>{ctpReferrals.length}</div>}
        </div>
        {ctpReferrals.length > 0 && (
          <ReferralInfo 
            referral={CaseManagementReferralAdapter.adapt(ctpReferrals[0], { 
              user,
              direction: 'outgoing',
              profileStage: 'PENDING',
              caseloadReferralIDs: [],
              caseloadUserID: user.id
            })} 
          />
        )}
        {ctpReferrals.length === 1 && (
          <Button 
            className={classes.ctpButton}
            onClick={onSendReferral}
          >
            Create New Referral
            <i className="far fa-plus" style={{ fontSize: 16 }} />
          </Button>
        )}
        {ctpReferrals.length > 1 && (
          <Button 
          className={classes.ctpButton}
          onClick={onViewAllReferrals}
        >
           See All Referrals
          <i className="far fa-chevron-down" style={{ fontSize: 16 }} />
        </Button>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={classes.skeletonWrapper}>
        <Skeleton variant="rounded" height={160} sx={{ borderRadius: "20px" }} />
      </div>
    );
  }

  if (!showBanner) {
    return null;
  }

  return ctpReferrals.length > 0 ? renderReferralCreated() : renderReferralRequired();
}

export default CareCoordinatorReferralBannerCard; 